/**
 * パスワードの再設定のアカウント名（メールアドレス）まわり（2026-10-09 core/reset-username・founder のつまずき）。
 *
 * つまずき: /forgot-password → /reset-password で新しいパスワードを決めたのに、/login で自動入力されなかった。
 *   原因 = /reset-password にアカウント名の欄が無く、パスワード管理が新しいパスワードをどのアカウントの物か結び付けられなかった。
 *   続けて「ログイン画面でメールを手で打つのが面倒」（founder・同日）。
 *
 * 直し方:
 *   1. 再設定の画面を開いたら POST /v1/developers/password/reset/account { token } → { email } でアカウントを聞く
 *      （token は使用済みにならない・サーバーは apps/server/src/modules/platform/recovery.ts）
 *   2. フォームに読み取り専用のアカウント名の欄（autocomplete="username"・usernameFieldProps）を置き、見出しの下に「New password for <email>」
 *   3. 成功したら同じタブの sessionStorage にメールを置き（rememberResetEmail）、/login がそれを欄に入れて消す（peek → clear）。
 *      完了の画面（/reset-password/done）を挟んでも、/login が読むまで残る
 *
 * WHY sessionStorage か（URL のクエリ ?email= にしない）: URL はブラウザの履歴・画面共有・アクセスログ・Referer に残る。
 *   sessionStorage は同じタブの中だけ・タブを閉じれば消える。localStorage（lastLogin.ts の「この端末に保存」）とは分ける
 *   = 「保存しない」を選んだ端末に、再設定のメールを勝手に残さない。
 * WHY 期限（RESET_EMAIL_TTL_MS）: 再設定の後にログインせずタブを放っておいた場合、後で開いた /login に古い値を出さない。
 *
 * React を import しない（node --test で直接読むため）。画面の hook（useResetAccount）は pages/AccountRecovery.tsx。
 */
import type { KvStorage } from './lastLogin.ts';

export const RESET_EMAIL_KEY = 'kumo.resetEmail.v1';
/** 30 分 = 再設定のリンクの期限と同じ。再設定 → ログインはふつう数十秒 */
export const RESET_EMAIL_TTL_MS = 30 * 60 * 1000;
// メールの欄と同じ上限（authForms.ts の emailFieldProps の maxLength・サーバーの email.max(254)）
const MAX_EMAIL = 254;

const isEmail = (v: unknown): v is string => typeof v === 'string' && v.length > 2 && v.length <= MAX_EMAIL && v.includes('@');

/** 同じタブのストレージ。使えない（プライベートブラウズ・無効）なら null = 渡さない（ログイン画面で打つだけ） */
export function tabStorage(): KvStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** POST /v1/developers/password/reset/account の応答を読む。形が違えば null（= アカウント名の欄を出さない今までの画面） */
export function readResetAccount(res: unknown): string | null {
  if (!res || typeof res !== 'object') return null;
  const email = (res as { email?: unknown }).email;
  return isEmail(email) ? email : null;
}

/** 再設定に成功した直後に呼ぶ。ログイン画面が読んで消す */
export function rememberResetEmail(storage: KvStorage | null, email: string, now = Date.now()): void {
  if (!storage || !isEmail(email)) return;
  try {
    storage.setItem(RESET_EMAIL_KEY, JSON.stringify({ email, at: now }));
  } catch {
    // 容量・無効: 渡せないだけ（ログイン画面で打てる）
  }
}

/**
 * 読むだけ（消さない）。呼ぶ側は描いた後に clearResetEmail で消す。
 * WHY 分けるか: React の StrictMode は useState の初期化を 2 回呼ぶ。読むと同時に消すと 2 回目が空になる（ResetDone.tsx の peek と同じ）
 */
export function peekResetEmail(storage: KvStorage | null, now = Date.now()): string | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(RESET_EMAIL_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { email?: unknown; at?: unknown };
    if (!isEmail(v.email) || typeof v.at !== 'number' || now - v.at > RESET_EMAIL_TTL_MS || now < v.at) return null;
    return v.email;
  } catch {
    return null;
  }
}

export function clearResetEmail(storage: KvStorage | null): void {
  try {
    storage?.removeItem(RESET_EMAIL_KEY);
  } catch {
    // 消せない = 期限（RESET_EMAIL_TTL_MS）で無効になる
  }
}

/**
 * 再設定の画面のアカウント名の欄（見える・読み取り専用）。
 * WHY 見える欄にするか（authForms.ts の hiddenUsernameProps の display:none ではなく）: 本人がどのアカウントのパスワードを決めているか
 *   画面で分かる方がよい。パスワード管理は readOnly の欄も読む。type=email にしない = 読み取り専用の欄にメールのキーボードは要らない
 */
export function usernameFieldProps(email: string) {
  return {
    id: 'username',
    name: 'username',
    type: 'text',
    autoComplete: 'username',
    value: email,
    readOnly: true,
    spellCheck: false,
    autoCapitalize: 'none'
  } as const;
}
